import React, { useCallback, useEffect, useState } from 'react'
import { CSmartTable } from '@coreui/react-pro'

import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'

// Every MCT game listing endpoint filters on these same keys.
const SEARCH_FIELDS = [
  { key: 'game_name', label: 'Game Name' },
  { key: 'id', label: 'Game ID' },
  { key: 'hub_name', label: 'Hub Name' },
  { key: 'hub_id', label: 'Hub ID' },
]

const BASE_COLUMNS = [
  { label: 'Game ID', key: 'id', filter: false, _style: { width: '7%' } },
  { label: 'Game Name', key: 'game_name', filter: false },
  { label: 'Hub Name', key: 'hub_name', filter: false },
  { label: 'Hub ID', key: 'hub_id', filter: false, _style: { width: '7%' } },
]

// One listing tab on the MCT Games page: search toolbar + server-paged table. The section
// passes only its own columns; the game/hub identity columns are shared. Clicking a game
// name opens the details panel.
const MctGameTable = ({
  fetcher,
  columns,
  scopedColumns = {},
  noItemsLabel,
  onOpenGame,
  onTotalChange,
  active = true,
}) => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('game_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [result, setResult] = useState()

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (activePage - 1) * itemsPerPage
      const params = new URLSearchParams()
      Object.keys(columnFilter).forEach((key) => {
        if (columnFilter[key]) params.append(key, columnFilter[key])
      })
      if (columnSorter?.column) {
        params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
      }
      const res = await fetcher(offset, itemsPerPage, activePage, params)
      setResult(res.data)
      onTotalChange?.(res.data?.total ?? 0)
    } catch (e) {
      ToastComponent(e?.response?.data?.message || 'Could not load games', 'error')
    } finally {
      setLoading(false)
    }
  }, [fetcher, activePage, columnFilter, columnSorter, itemsPerPage, onTotalChange])

  // Sections load the first time their tab is opened, then keep their data.
  useEffect(() => {
    if (active) fetchData()
  }, [active, fetchData])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
      />

      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <CSmartTable
          columns={[...BASE_COLUMNS, ...columns]}
          columnSorter={{ external: true }}
          noItemsLabel={noItemsLabel}
          scopedColumns={{
            id: (item) => <td className="um-muted">{item.id}</td>,
            game_name: (item) => (
              <td>
                <span
                  role="button"
                  className="fw-semibold text-primary"
                  title="View game details"
                  onClick={() => onOpenGame(item)}
                >
                  {item.game_name || '-'}
                </span>
              </td>
            ),
            hub_name: (item) => <td>{item.hub_name || '-'}</td>,
            hub_id: (item) => <td className="um-muted">{item.hub_id ?? '-'}</td>,
            ...scopedColumns,
          }}
          items={result?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage,
            pages: Math.ceil(result?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={setActivePage}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
          onSorterChange={setColumnSorter}
        />
      </div>
    </>
  )
}

export default MctGameTable
